/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../../config/enum';
import DefaultApiParameters from '../../../enum/DefaultApiParameters';
import { ApiRequest } from '../../../interface/common.interface';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
} from '../../../interface/filter.interface';
import {
  ClearFilters,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../../shared-functions/CommonFilters';
import ocpiApi from '../../../utils/api/ocpi';

/**
 * Dispatches a notification message using the shared notification slice's action type.
 * Avoids a circular import on the notification slice from within this one.
 * @param {any} dispatch - The thunk dispatch function.
 * @param {any} message - The message to show.
 * @param {any} type - The notification type.
 */
const notify = (dispatch: any, message: string, type: 'success' | 'error') => {
  dispatch({
    type: 'notification/setNotificationMessage',
    payload: { status: true, message, type },
  });
};

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

interface EmspTokensRequest extends ApiRequest {
  countryCode: string;
  partyId: string;
  counterPartyId?: string;
  counterCountryCode?: string;
}

interface FetchEmspTokensResponse {
  results: any[];
  limit: number;
  page: number;
}

/**
 * Fetches a page of OCPI tokens for an eMSP role from the internal gateway.
 */
export const fetchEmspTokens = createAsyncThunk<FetchEmspTokensResponse, EmspTokensRequest>(
  'emsp-tokens/fetchEmspTokens',
  async (
    { countryCode, partyId, counterPartyId, counterCountryCode, page, limit },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const resolvedPage = page || DefaultApiParameters.page;
      const resolvedLimit = limit || DefaultApiParameters.limit;
      const response = await ocpiApi.getInternalEmspTokens(
        countryCode,
        partyId,
        counterPartyId || '',
        counterCountryCode || '',
        resolvedPage,
        resolvedLimit,
      );
      const results = response?.data?.data ?? response?.data ?? [];
      return {
        results: Array.isArray(results) ? results : [],
        limit: resolvedLimit,
        page: resolvedPage,
      };
    } catch (error: any) {
      notify(dispatch, error?.response?.data?.message || 'Failed to fetch OCPI tokens.', 'error');
      return rejectWithValue(error?.response?.data);
    }
  },
);

/**
 * Broadcasts all eMSP tokens to connected partners.
 */
export const broadcastEmspTokens = createAsyncThunk<
  void,
  { countryCode: string; partyId: string }
>('emsp-tokens/broadcastEmspTokens', async ({ countryCode, partyId }, { dispatch }) => {
  try {
    const response = await ocpiApi.broadcastInternalEmspTokens(countryCode, partyId);
    notify(
      dispatch,
      response?.data?.status_message || response?.status_message || 'Broadcast triggered.',
      'success',
    );
  } catch (error: any) {
    notify(dispatch, error?.message || 'Failed to broadcast.', 'error');
  }
});

/**
 * Resolves the filter list for the eMSP tokens table. Filter options are known upfront (there
 * is no backend filter-list endpoint for OCPI tokens), so this only exists to satisfy
 * CustomTable's filterListToDispatch contract.
 */
export const fetchEmspTokenFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'emsp-tokens/fetchEmspTokenFilters',
  async () => availableFilters,
);

interface EMSPTokensInitialState {
  tokens: any[];
  limit: number;
  page: number;
  totalPages: number;
  totalResults: number;
  loading: boolean;
  filters: CommonFilterProperties[];
  filterListLoading: boolean;
  isFilterUpdated: boolean;
  sortBy: string;
  sortField: string;
}

const initialState: EMSPTokensInitialState = {
  tokens: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const emspTokensSlice = createSlice({
  name: 'emsp-tokens',
  initialState,
  reducers: {
    /**
     * Update EMSP Tokens filters
     * @param {any} state - The current slice state.
     * @param {any} action - The action payload containing filter updates.
     */
    updateEMSPTokensFilters: (state: any, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current slice state.
     */
    disableEmspTokensFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state - The current slice state.
     * @param {any} action - The action payload containing sorting criteria.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Extra reducers to handle thunk requests.
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmspTokens.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEmspTokens.fulfilled, (state, action) => {
        state.loading = false;
        state.tokens = action.payload.results;
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalResults = action.payload.results.length;
        state.totalPages = Math.max(
          1,
          Math.ceil(action.payload.results.length / action.payload.limit),
        );
      })
      .addCase(fetchEmspTokens.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchEmspTokenFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(fetchEmspTokenFilters.fulfilled, (state, action) => {
        state.filterListLoading = false;
        state.filters = action.payload;
      })
      .addCase(fetchEmspTokenFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const { updateEMSPTokensFilters, disableEmspTokensFilterApplied, setSortData } =
  emspTokensSlice.actions;

export default emspTokensSlice.reducer;
